import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, ChevronDown, ChevronUp, Mail, Phone, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const fieldLabels = [
  ["situazione", "Situazione attuale"],
  ["cambiamento", "Cambiamento desiderato"],
  ["progetto", "Progetto / attività / community"],
  ["da_zero", "Se parte da zero, vuole costruire"],
  ["fede", "Rapporto con la fede"],
  ["momento", "Perché è il momento giusto"],
  ["disponibilita_call", "Disponibilità call"],
];

const CandidaturaCard = ({ c }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="ub-card rounded-xl p-6" data-testid={`candidatura-${c.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-4 text-left" data-testid={`candidatura-toggle-${c.id}`}>
        <div>
          <p className="font-display text-lg text-[#f3e5ab]">{c.nome} {c.cognome} <span className="text-sm text-[#7a7367] font-sans">— {c.eta} anni, {c.lavoro}</span></p>
          <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-xs text-[#b8b2a7]">
            <span className="inline-flex items-center gap-1.5"><Mail size={12} className="text-[#d4af37]" /> {c.email}</span>
            <span className="inline-flex items-center gap-1.5"><Phone size={12} className="text-[#d4af37]" /> {c.whatsapp}</span>
            <span className="inline-flex items-center gap-1.5"><MapPin size={12} className="text-[#d4af37]" /> {c.citta}</span>
          </div>
          <p className="text-[10px] text-[#7a7367] mt-2">{new Date(c.created_at).toLocaleString("it-IT")}</p>
        </div>
        {open ? <ChevronUp size={18} className="text-[#d4af37] shrink-0" /> : <ChevronDown size={18} className="text-[#d4af37] shrink-0" />}
      </button>
      {open && (
        <div className="mt-5 pt-5 border-t border-[#d4af37]/15 space-y-4">
          {fieldLabels.map(([k, label]) => c[k] && (
            <div key={k}>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#d4af37]/80 font-bold">{label}</p>
              <p className="text-sm text-[#b8b2a7] mt-1 whitespace-pre-wrap">{c[k]}</p>
            </div>
          ))}
          <a
            href={`https://wa.me/${c.whatsapp.replace(/[^0-9]/g, "")}`}
            target="_blank"
            rel="noopener noreferrer"
            className="cta-gold rounded-full px-5 py-2.5 text-[11px] uppercase inline-block"
            data-testid={`candidatura-whatsapp-${c.id}`}
          >
            Contatta su WhatsApp
          </a>
        </div>
      )}
    </div>
  );
};

export default function AdminPage() {
  const [auth, setAuth] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);

  const loadCandidature = async () => {
    try {
      const { data } = await axios.get(`${API}/candidature`);
      setList(data);
    } catch {
      toast.error("Errore nel caricamento delle candidature.");
    }
  };

  useEffect(() => {
    axios.get(`${API}/auth/me`)
      .then(() => { setAuth(true); loadCandidature(); })
      .catch(() => setAuth(false));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/login`, { email, password });
      setAuth(true);
      loadCandidature();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Credenziali non valide.");
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`);
    setAuth(false);
    setList([]);
  };

  if (auth === null) {
    return <div className="min-h-screen bg-[#080706] flex items-center justify-center text-[#7a7367] text-sm">Caricamento...</div>;
  }

  if (!auth) {
    return (
      <div className="min-h-screen bg-[#080706] grain flex items-center justify-center px-5">
        <form onSubmit={login} className="ub-card rounded-2xl p-8 sm:p-10 w-full max-w-md" data-testid="admin-login-form">
          <div className="flex items-center gap-3 mb-2">
            <Lock size={18} className="text-[#d4af37]" />
            <p className="font-display text-lg tracking-[0.2em] gold-text font-bold">AREA RISERVATA</p>
          </div>
          <p className="text-xs text-[#7a7367] mb-8">Tempio Vivo — Candidature</p>
          <label className="block mb-5">
            <span className="text-[11px] uppercase tracking-[0.2em] text-[#b8b2a7] font-semibold">Email</span>
            <input data-testid="admin-email" type="email" className="ub-input w-full rounded-lg px-4 py-3 text-sm mt-2" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@..." />
          </label>
          <label className="block mb-8">
            <span className="text-[11px] uppercase tracking-[0.2em] text-[#b8b2a7] font-semibold">Password</span>
            <input data-testid="admin-password" type="password" className="ub-input w-full rounded-lg px-4 py-3 text-sm mt-2" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
          </label>
          <button type="submit" disabled={loading} data-testid="admin-login-submit" className="cta-gold rounded-full w-full py-4 text-xs uppercase disabled:opacity-60">
            {loading ? "Accesso..." : "Accedi"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080706] grain px-5 sm:px-10 py-10">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <div>
            <p className="font-display text-xl tracking-[0.2em] gold-text font-bold">CANDIDATURE</p>
            <p className="text-xs text-[#7a7367] mt-1" data-testid="admin-count">{list.length} candidature ricevute</p>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="cta-ghost rounded-full px-5 py-2.5 text-[11px] uppercase inline-flex items-center gap-2">
            <LogOut size={14} /> Esci
          </button>
        </div>
        <div className="space-y-4" data-testid="admin-candidature-list">
          {list.length === 0 && <p className="text-sm text-[#7a7367] text-center py-20">Nessuna candidatura ricevuta finora.</p>}
          {list.map((c) => <CandidaturaCard key={c.id} c={c} />)}
        </div>
      </div>
    </div>
  );
}
